import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toCamel } from './case';
import { supabase } from './supabase';

/* ---- đăng ký realtime dùng chung: mỗi bảng chỉ một kênh, dù nhiều màn hình cùng dùng ---- */
const channels = new Map<string, { n: number; stop: () => void }>();
/** Khung nhìn (view) không có sự kiện realtime: làm mới cùng lúc với bảng gốc. */
const ALSO: Record<string, string[]> = { employees: ['employee_directory'] };

function subscribeTable(table: string, qc: QueryClient): () => void {
  let entry = channels.get(table);
  if (!entry) {
    // Gộp các sự kiện dồn dập (vd. import hàng nghìn dòng) thành một lần tải lại.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ch = supabase().channel('rt-' + table).on('postgres_changes', { event: '*', schema: 'public', table }, () => {
      clearTimeout(timer);
      timer = setTimeout(() => { for (const k of [table, ...(ALSO[table] ?? [])]) void qc.invalidateQueries({ queryKey: [k] }); }, 400);
    }).subscribe();
    entry = { n: 0, stop: () => { clearTimeout(timer); void supabase().removeChannel(ch); } };
    channels.set(table, entry);
  }
  entry.n++;
  return () => {
    const e = channels.get(table);
    if (e && --e.n <= 0) { e.stop(); channels.delete(table); }
  };
}

export function useRealtime(table: string): void {
  const qc = useQueryClient();
  useEffect(() => subscribeTable(table, qc), [table, qc]);
}

export interface RowsOptions { order?: string; ascending?: boolean; enabled?: boolean; /** bảng để nghe realtime (mặc định chính bảng đang đọc) */ realtime?: string }

const PAGE = 1000;   // Supabase/PostgREST mặc định trả tối đa 1000 dòng mỗi lần → đọc từng trang cho đến hết

export async function fetchAllRows(table: string, order?: string, ascending = true): Promise<Record<string, unknown>[]> {
  const all: Record<string, unknown>[] = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase().from(table).select('*');
    if (order) q = q.order(order, { ascending });
    q = q.order('id', { ascending: true }).range(from, from + PAGE - 1);   // khóa phụ để phân trang ổn định
    const { data, error } = await q;
    if (error) throw error;
    all.push(...(data as Record<string, unknown>[]));
    if (!data || data.length < PAGE) break;
  }
  return all;
}

/** Đọc cả bảng (đã qua RLS), đổi tên cột sang camelCase, tự làm mới khi dữ liệu đổi (realtime). */
export function useRows<T>(table: string, opts: RowsOptions = {}) {
  useRealtime(opts.realtime ?? table);
  return useQuery({
    queryKey: [table],
    enabled: opts.enabled ?? true,
    queryFn: async (): Promise<T[]> => (await fetchAllRows(table, opts.order, opts.ascending)).map(r => toCamel<T>(r)),
  });
}

/* ---- thiết lập dùng chung (app_settings) ---- */
export type CompanySettings = Record<string, unknown> & {
  nameVi?: string; nameEn?: string; appUrl?: string; mailApp?: string; mailCc?: string; docOrg?: string;
};

export function useSetting<T extends Record<string, unknown> = CompanySettings>(key: string) {
  useRealtime('app_settings');
  return useQuery({
    queryKey: ['app_settings', key],
    queryFn: async (): Promise<T> => {
      const { data, error } = await supabase().from('app_settings').select('value').eq('key', key).maybeSingle();
      if (error) throw error;
      return ((data?.value as T) ?? ({} as T));
    },
  });
}

/** Gộp `patch` vào thiết lập hiện có rồi ghi lại (đọc–sửa–ghi). */
export async function saveSetting(key: string, patch: Record<string, unknown>, updatedBy = ''): Promise<void> {
  const { data, error } = await supabase().from('app_settings').select('value').eq('key', key).maybeSingle();
  if (error) throw error;
  const value = { ...((data?.value as Record<string, unknown>) || {}), ...patch };
  const { error: e2 } = await supabase().from('app_settings').upsert({ key, value, updated_by: updatedBy }, { onConflict: 'key' });
  if (e2) throw e2;
}

/** Ghi nhiều dòng theo lô (upsert theo `onConflict`), báo tiến độ. Trả về số dòng đã ghi. */
export async function upsertChunks(table: string, rows: Record<string, unknown>[], onConflict: string, onProgress?: (done: number, total: number) => void, size = 200): Promise<number> {
  let done = 0;
  for (let i = 0; i < rows.length; i += size) {
    const part = rows.slice(i, i + size);
    const { error } = await supabase().from(table).upsert(part, { onConflict });
    if (error) throw error;
    done += part.length;
    onProgress?.(done, rows.length);
  }
  return done;
}

const HAS_VI = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i;

interface DbErrorLike { message?: string; code?: string; details?: string }

/** Đổi lỗi kỹ thuật của CSDL thành câu tiếng Việt dễ hiểu. Lỗi do chính hệ thống ném ra (đã là tiếng Việt) giữ nguyên. */
export function errMsg(e: unknown, fallback = 'Có lỗi xảy ra, thử lại sau.'): string {
  const x = (typeof e === 'object' && e ? e : {}) as DbErrorLike;
  const msg = x.message || (e instanceof Error ? e.message : '');
  if (!msg) return fallback;
  if (HAS_VI.test(msg)) return msg;                      // thông báo do chính hệ thống ném ra (đã là tiếng Việt): giữ nguyên
  if (x.code === '42501' && /row-level security|permission denied/i.test(msg)) return 'Bạn không có quyền thực hiện thao tác này.';
  if (/permission denied/i.test(msg)) return 'Bạn không có quyền thực hiện thao tác này.';
  if (x.code === '23505' || /duplicate key/i.test(msg)) return 'Dữ liệu bị trùng (mã hoặc khóa đã tồn tại).';
  if (x.code === '23503' && /still referenced|violates foreign key/i.test(msg)) return 'Đang được dùng ở nơi khác (chứng từ, hợp đồng…), không xoá được.';
  if (/Failed to fetch|NetworkError|network/i.test(msg)) return 'Mất kết nối mạng. Thử lại sau.';
  if (/^(new row|null value|invalid input|value too long|check constraint)/i.test(msg) || /violates (check|not-null)/i.test(msg)) return 'Dữ liệu không hợp lệ, kiểm tra lại các ô đã nhập.';
  return msg;
}
